/**
 * Input distributions (PRD §4.1).
 *
 * Every distribution exposes an inverse CDF (`quantile`). Sampling is therefore
 * always inverse-transform: exactly one uniform in, one variate out. That is
 * what lets the counter-based PRNG address draws by (iteration, slot) — a
 * rejection sampler would consume a variable number of uniforms and break
 * reproducibility across worker counts (PRD §6.2).
 *
 * It also means correlation composes for free: feed the copula's uniform into
 * `quantile` and any marginal can carry any correlation structure.
 */

import { normalCdf, normalInv, normalPdf } from './special.ts';

export type Distribution =
  | { kind: 'fixed'; value: number }
  | { kind: 'uniform'; min: number; max: number }
  | { kind: 'normal'; mu: number; sigma: number }
  | { kind: 'truncnormal'; mu: number; sigma: number; min: number; max: number }
  | { kind: 'triangular'; min: number; mode: number; max: number }
  | { kind: 'lognormal'; muLog: number; sigmaLog: number }
  | { kind: 'discrete'; values: number[]; weights: number[] }
  /**
   * Measured data (PRD §4.1). `samples` MUST be sorted ascending — the inverse
   * CDF indexes into it directly, and sorting per draw would be catastrophic in
   * the hot loop. Build these with `empiricalFromSamples`, which sorts; the
   * invariant is then checked by `validate`.
   */
  | { kind: 'empirical'; samples: number[] };

/**
 * How many sigmas of an unbounded distribution to treat as its "edge" for
 * worst-case analysis. WCA on a Normal is otherwise infinite and meaningless;
 * the clamp must be explicit and reported (PRD §4.5).
 */
export const DEFAULT_SIGMA_CLAMP = 3;

export class DistributionError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'DistributionError';
  }
}

/** Validate parameters, throwing on anything that would silently produce NaN. */
export function validate(d: Distribution, label = 'distribution'): void {
  const bad = (msg: string) => {
    throw new DistributionError(`${label}: ${msg}`);
  };

  switch (d.kind) {
    case 'fixed':
      if (!Number.isFinite(d.value)) bad('value must be finite');
      break;
    case 'uniform':
      if (!Number.isFinite(d.min) || !Number.isFinite(d.max)) bad('bounds must be finite');
      if (d.max < d.min) bad('max must be >= min');
      break;
    case 'normal':
      if (!Number.isFinite(d.mu)) bad('mu must be finite');
      if (!(d.sigma >= 0) || !Number.isFinite(d.sigma)) bad('sigma must be finite and >= 0');
      break;
    case 'truncnormal':
      if (!(d.sigma > 0)) bad('sigma must be > 0');
      if (d.max <= d.min) bad('max must be > min');
      if (normalCdf((d.max - d.mu) / d.sigma) - normalCdf((d.min - d.mu) / d.sigma) <= 0) {
        bad('truncation bounds exclude essentially all probability mass');
      }
      break;
    case 'triangular':
      if (d.max < d.min) bad('max must be >= min');
      if (d.mode < d.min || d.mode > d.max) bad('mode must lie within [min, max]');
      break;
    case 'lognormal':
      if (!Number.isFinite(d.muLog)) bad('muLog must be finite');
      if (!(d.sigmaLog >= 0)) bad('sigmaLog must be >= 0');
      break;
    case 'discrete': {
      if (d.values.length === 0) bad('needs at least one value');
      if (d.values.length !== d.weights.length) bad('values and weights length mismatch');
      const total = d.weights.reduce((a, b) => a + b, 0);
      if (!(total > 0)) bad('weights must sum to > 0');
      if (d.weights.some((w) => w < 0)) bad('weights must be non-negative');
      break;
    }
    case 'empirical': {
      if (d.samples.length < 2) bad('needs at least 2 measured samples');
      for (let i = 0; i < d.samples.length; i++) {
        if (!Number.isFinite(d.samples[i])) bad(`sample ${i} is not a finite number`);
        // The sorted invariant is load-bearing: quantile() indexes directly.
        if (i > 0 && d.samples[i] < d.samples[i - 1]) {
          bad('samples must be sorted ascending — build with empiricalFromSamples()');
        }
      }
      break;
    }
  }
}

/** Inverse CDF. `u` must be in [0,1). */
export function quantile(d: Distribution, u: number): number {
  switch (d.kind) {
    case 'fixed':
      return d.value;

    case 'uniform':
      return d.min + u * (d.max - d.min);

    case 'normal':
      return d.mu + d.sigma * normalInv(u);

    case 'truncnormal': {
      const lo = normalCdf((d.min - d.mu) / d.sigma);
      const hi = normalCdf((d.max - d.mu) / d.sigma);
      const v = d.mu + d.sigma * normalInv(lo + u * (hi - lo));
      // Guard the rare case where round-trip rounding lands a hair outside.
      return v < d.min ? d.min : v > d.max ? d.max : v;
    }

    case 'triangular': {
      const span = d.max - d.min;
      if (span === 0) return d.min;
      const split = (d.mode - d.min) / span;
      if (u < split) {
        return d.min + Math.sqrt(u * span * (d.mode - d.min));
      }
      return d.max - Math.sqrt((1 - u) * span * (d.max - d.mode));
    }

    case 'lognormal':
      return Math.exp(d.muLog + d.sigmaLog * normalInv(u));

    case 'discrete': {
      const total = d.weights.reduce((a, b) => a + b, 0);
      let acc = 0;
      const target = u * total;
      for (let i = 0; i < d.values.length; i++) {
        acc += d.weights[i];
        if (target < acc) return d.values[i];
      }
      return d.values[d.values.length - 1];
    }

    case 'empirical': {
      // Linear interpolation between order statistics. This is deliberately
      // NOT kernel-smoothed: a kernel would need a second random draw per
      // sample, which breaks the one-uniform-per-variable mapping the
      // counter-based PRNG relies on (PRD §6.2). Interpolating already removes
      // the raw ECDF staircase, at no cost to reproducibility.
      const s = d.samples;
      const n = s.length;
      const pos = u * n - 0.5;
      if (pos <= 0) return s[0];
      if (pos >= n - 1) return s[n - 1];
      const i = Math.floor(pos);
      const frac = pos - i;
      return s[i] + frac * (s[i + 1] - s[i]);
    }
  }
}

/** Analytic mean. */
export function mean(d: Distribution): number {
  switch (d.kind) {
    case 'fixed':
      return d.value;
    case 'uniform':
      return (d.min + d.max) / 2;
    case 'normal':
      return d.mu;
    case 'truncnormal': {
      const a = (d.min - d.mu) / d.sigma;
      const b = (d.max - d.mu) / d.sigma;
      const z = normalCdf(b) - normalCdf(a);
      return d.mu + (d.sigma * (normalPdf(a) - normalPdf(b))) / z;
    }
    case 'triangular':
      return (d.min + d.mode + d.max) / 3;
    case 'lognormal':
      return Math.exp(d.muLog + (d.sigmaLog * d.sigmaLog) / 2);
    case 'discrete': {
      const total = d.weights.reduce((a, b) => a + b, 0);
      let m = 0;
      for (let i = 0; i < d.values.length; i++) m += (d.weights[i] / total) * d.values[i];
      return m;
    }
    case 'empirical': {
      let m = 0;
      for (const x of d.samples) m += x;
      return m / d.samples.length;
    }
  }
}

/** Analytic variance. */
export function variance(d: Distribution): number {
  switch (d.kind) {
    case 'fixed':
      return 0;
    case 'uniform':
      return (d.max - d.min) ** 2 / 12;
    case 'normal':
      return d.sigma * d.sigma;
    case 'truncnormal': {
      const a = (d.min - d.mu) / d.sigma;
      const b = (d.max - d.mu) / d.sigma;
      const z = normalCdf(b) - normalCdf(a);
      const pa = normalPdf(a);
      const pb = normalPdf(b);
      const term = (a * pa - b * pb) / z;
      const ratio = (pa - pb) / z;
      return d.sigma * d.sigma * (1 + term - ratio * ratio);
    }
    case 'triangular': {
      const { min: a, mode: c, max: b } = d;
      return (a * a + b * b + c * c - a * b - a * c - b * c) / 18;
    }
    case 'lognormal': {
      const s2 = d.sigmaLog * d.sigmaLog;
      return (Math.exp(s2) - 1) * Math.exp(2 * d.muLog + s2);
    }
    case 'discrete': {
      const total = d.weights.reduce((a, b) => a + b, 0);
      const m = mean(d);
      let v = 0;
      for (let i = 0; i < d.values.length; i++) {
        v += (d.weights[i] / total) * (d.values[i] - m) ** 2;
      }
      return v;
    }
    case 'empirical': {
      // Sample variance: these are measurements of a population, not the
      // population itself, so the n-1 correction is the honest choice.
      const m = mean(d);
      let v = 0;
      for (const x of d.samples) v += (x - m) ** 2;
      return v / Math.max(1, d.samples.length - 1);
    }
  }
}

export function stdDev(d: Distribution): number {
  return Math.sqrt(variance(d));
}

/**
 * The interval used as this variable's box for worst-case analysis.
 *
 * Unbounded distributions are clamped at +/- `sigmaClamp` sigma; the clamp is
 * returned to the caller so the report can state it rather than implying the
 * bound is absolute.
 */
export function bounds(
  d: Distribution,
  sigmaClamp = DEFAULT_SIGMA_CLAMP,
): { min: number; max: number; clamped: boolean } {
  switch (d.kind) {
    case 'fixed':
      return { min: d.value, max: d.value, clamped: false };
    case 'uniform':
      return { min: d.min, max: d.max, clamped: false };
    case 'normal':
      return {
        min: d.mu - sigmaClamp * d.sigma,
        max: d.mu + sigmaClamp * d.sigma,
        clamped: d.sigma > 0,
      };
    case 'truncnormal':
      return { min: d.min, max: d.max, clamped: false };
    case 'triangular':
      return { min: d.min, max: d.max, clamped: false };
    case 'lognormal':
      return {
        min: Math.exp(d.muLog - sigmaClamp * d.sigmaLog),
        max: Math.exp(d.muLog + sigmaClamp * d.sigmaLog),
        clamped: d.sigmaLog > 0,
      };
    case 'discrete':
      return { min: Math.min(...d.values), max: Math.max(...d.values), clamped: false };
    case 'empirical':
      // Measured extremes are real observations, not a modelling clamp — but
      // they are also only as wide as the sample happened to be.
      return { min: d.samples[0], max: d.samples[d.samples.length - 1], clamped: false };
  }
}

/** The point at which sensitivities are evaluated for WCA and RSS. */
export function nominal(d: Distribution): number {
  switch (d.kind) {
    case 'fixed':
      return d.value;
    case 'triangular':
      return d.mode;
    case 'lognormal':
      return Math.exp(d.muLog); // median, the natural centre for a lognormal
    case 'empirical':
      return quantile(d, 0.5); // median resists a stray outlier in measured data
    default:
      return mean(d);
  }
}

// --- Constructors matching how engineers actually state tolerances ----------

/**
 * "100k +/- 1%" with no further qualification.
 *
 * Uniform is the conventional default, but it *is* an assumption — bin-sorted
 * passives are often bimodal and many parts are tight-normal well inside the
 * band (PRD §4.1). Callers should surface which default was applied.
 */
export function uniformFromTolerance(nominalValue: number, relativeTol: number): Distribution {
  const half = Math.abs(nominalValue * relativeTol);
  return { kind: 'uniform', min: nominalValue - half, max: nominalValue + half };
}

/**
 * "+/- tol at k sigma".
 *
 * `sigmaLevel` is a sigma level, not a Cpk. Cpk is an output capability index
 * that also folds in mean shift and is never an input parameterisation
 * (PRD §4.1 terminology note).
 */
export function normalFromTolerance(
  nominalValue: number,
  absoluteTol: number,
  sigmaLevel: 3 | 4 | 6 = 3,
): Distribution {
  return { kind: 'normal', mu: nominalValue, sigma: Math.abs(absoluteTol) / sigmaLevel };
}

/**
 * Build an empirical distribution from measured data.
 *
 * Sorts, because `quantile` indexes the array directly and the sorted order is
 * a load-bearing invariant rather than a convenience.
 */
export function empiricalFromSamples(samples: readonly number[]): Distribution {
  const clean = samples.filter((x) => Number.isFinite(x));
  if (clean.length < 2) {
    throw new DistributionError('empirical: needs at least 2 finite samples');
  }
  return { kind: 'empirical', samples: [...clean].sort((a, b) => a - b) };
}

/**
 * Scale a distribution's spread about its centre, holding the centre fixed.
 *
 * This is what "what if I halved this tolerance?" means mechanically, and it is
 * the basis of the tolerance-cost ranking (PRD §4.6 tier 3). Scaling about the
 * NOMINAL rather than the mean matters for skewed distributions: tightening a
 * tolerance in practice means holding the target and narrowing the band, not
 * shifting the target to wherever the mean happened to be.
 *
 * `factor` of 0.5 halves the spread; 2 doubles it; 0 collapses to a constant.
 */
export function scaleSpread(d: Distribution, factor: number): Distribution {
  const f = Math.max(0, factor);
  const c = nominal(d);
  const about = (v: number) => c + f * (v - c);

  // A zero-width band is a constant, and several kinds cannot represent one:
  // a truncated normal with min === max has no probability mass to normalise
  // by and evaluates to NaN. Collapsing to `fixed` says the same thing and is
  // representable by every consumer.
  if (f === 0) return { kind: 'fixed', value: c };

  switch (d.kind) {
    case 'fixed':
      return d;
    case 'uniform':
      return { kind: 'uniform', min: about(d.min), max: about(d.max) };
    case 'normal':
      return { kind: 'normal', mu: d.mu, sigma: d.sigma * f };
    case 'truncnormal':
      // `mu` is scaled about the centre alongside the bounds. If it were held
      // fixed while the bounds moved, an asymmetric truncation would change
      // SHAPE as well as width: scaling all three together leaves every
      // truncation z-score (min - mu)/sigma exactly invariant, so the
      // distribution narrows without deforming.
      return {
        kind: 'truncnormal',
        mu: about(d.mu),
        sigma: d.sigma * f,
        min: about(d.min),
        max: about(d.max),
      };
    case 'triangular':
      return {
        kind: 'triangular',
        min: about(d.min),
        mode: d.mode,
        max: about(d.max),
      };
    case 'lognormal':
      // Spread lives in log space, so scaling sigmaLog keeps the distribution
      // lognormal and holds the median fixed.
      return { kind: 'lognormal', muLog: d.muLog, sigmaLog: d.sigmaLog * f };
    case 'discrete':
      return { kind: 'discrete', values: d.values.map(about), weights: [...d.weights] };
    case 'empirical':
      // Scaling preserves the measured SHAPE while narrowing the band, which is
      // what a process improvement usually looks like.
      return { kind: 'empirical', samples: d.samples.map(about) };
  }
}

/** A normal truncated at its stated tolerance band, i.e. 100% screened parts. */
export function screenedNormal(
  nominalValue: number,
  absoluteTol: number,
  sigmaLevel: 3 | 4 | 6 = 3,
): Distribution {
  const tol = Math.abs(absoluteTol);
  return {
    kind: 'truncnormal',
    mu: nominalValue,
    sigma: tol / sigmaLevel,
    min: nominalValue - tol,
    max: nominalValue + tol,
  };
}
